import { useEffect, useState } from 'react'

/**
 * Small read-only card showing the holdings-pricing health signals:
 * coverage %, stuck count, how many repriced in the last 24h. Serves
 * as a trust indicator — at a glance Simon knows whether the NAV
 * shown above is based on fresh marks or days-old seed values.
 */

interface DataQuality {
  total_holdings: number
  priceable: number
  stuck: number
  correctly_skipped: number
  coverage_pct: number
  freshness: {
    fresh_24h: number
    stale_24_48h: number
    stale_48h_7d: number
    very_stale: number
  }
  latest_reprice_at: string | null
  unconvertible_currency_count?: number
  unconvertible_currency_examples?: Array<{ id: number; name: string; currency: string }>
  zero_value_count?: number
  zero_value_examples?: Array<{ id: number; name: string; value: number }>
}

interface StaleHoldings {
  checked_at: string
  flagged_count: number
  total_value_flagged_gbp: number
  holdings: Array<{
    name: string
    symbol: string
    stored_price: number
    market_price: number
    ratio: number
    hint: string
    source: string
    value_gbp: number
  }>
  note: string
}

interface Unpriceable {
  count: number
  unpriceable_value_gbp: number
  error?: string
  holdings: Array<{
    isin: string
    name: string
    value_gbp: number
    positions: number
    last_price_update: string | null
  }>
}

function _agoShort(iso: string | null): string {
  if (!iso) return 'never'
  try {
    const diff = Math.max(0, Date.now() - new Date(iso).getTime())
    const m = Math.floor(diff / 60_000)
    if (m < 60) return `${m}m`
    const h = Math.floor(m / 60)
    if (h < 24) return `${h}h`
    return `${Math.floor(h / 24)}d`
  } catch { return '—' }
}

export function DataQualityPanel() {
  const [data, setData] = useState<DataQuality | null>(null)
  const [divergences, setDivergences] = useState<StaleHoldings | null>(null)
  const [unpriceable, setUnpriceable] = useState<Unpriceable | null>(null)

  useEffect(() => {
    let alive = true
    const fetchOnce = async () => {
      try {
        const res = await fetch('/api/accounts/data-quality', {
          headers: { Authorization: `Bearer ${localStorage.getItem('av-access-token') || ''}` },
        })
        if (!res.ok || !alive) return
        setData(await res.json())
      } catch { /* silent */ }
    }
    fetchOnce()
    const t = setInterval(fetchOnce, 60_000)
    return () => { alive = false; clearInterval(t) }
  }, [])

  useEffect(() => {
    let alive = true
    const fetchOnce = async () => {
      try {
        const res = await fetch('/api/pricing-health/stale-holdings', {
          headers: { Authorization: `Bearer ${localStorage.getItem('av-access-token') || ''}` },
        })
        if (!res.ok || !alive) return
        setDivergences(await res.json())
      } catch { /* silent */ }
    }
    fetchOnce()
    const t = setInterval(fetchOnce, 60_000)
    return () => { alive = false; clearInterval(t) }
  }, [])

  useEffect(() => {
    let alive = true
    const fetchOnce = async () => {
      try {
        const res = await fetch('/api/pricing-health/unpriceable', {
          headers: { Authorization: `Bearer ${localStorage.getItem('av-access-token') || ''}` },
        })
        if (!res.ok || !alive) return
        setUnpriceable(await res.json())
      } catch { /* silent */ }
    }
    fetchOnce()
    // These never recover on their own, so a slow poll is plenty.
    const t = setInterval(fetchOnce, 600_000)
    return () => { alive = false; clearInterval(t) }
  }, [])

  if (!data) return null

  const coverageClass =
    data.coverage_pct >= 95 ? 'text-emerald-light' :
    data.coverage_pct >= 75 ? 'text-amber-light' :
    'text-rose-light'

  const freshnessClass =
    data.freshness.very_stale === 0 && data.freshness.stale_48h_7d === 0 ? 'text-emerald-light' :
    data.freshness.very_stale === 0 ? 'text-amber-light' :
    'text-rose-light'

  return (
    <div className="card p-3 border-border/60 bg-surface-hover/20 text-xs">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-text-muted">
        <span>
          <span className="text-text-secondary font-medium">Holdings:</span>{' '}
          <span className="text-text-primary font-mono">{data.total_holdings}</span>
        </span>
        <span>
          <span className="text-text-secondary font-medium">Coverage:</span>{' '}
          <span className={`font-mono font-semibold ${coverageClass}`}>{data.coverage_pct}%</span>{' '}
          <span className="text-text-muted text-[10px]">
            ({data.priceable} priceable, {data.stuck} stuck, {data.correctly_skipped} skipped)
          </span>
        </span>
        <span>
          <span className="text-text-secondary font-medium">Freshness:</span>{' '}
          <span className={`font-mono font-semibold ${freshnessClass}`}>
            {data.freshness.fresh_24h} fresh
          </span>
          {data.freshness.stale_24_48h > 0 && (
            <span className="text-amber-light font-mono"> · {data.freshness.stale_24_48h} 24-48h</span>
          )}
          {data.freshness.stale_48h_7d > 0 && (
            <span className="text-rose-light font-mono"> · {data.freshness.stale_48h_7d} 2-7d</span>
          )}
          {data.freshness.very_stale > 0 && (
            <span className="text-rose font-mono"> · {data.freshness.very_stale} &gt;7d</span>
          )}
        </span>
        <span>
          <span className="text-text-secondary font-medium">Latest mark:</span>{' '}
          <span className="text-text-primary font-mono">{_agoShort(data.latest_reprice_at)} ago</span>
        </span>
        {(data.unconvertible_currency_count ?? 0) > 0 && (
          <span
            className="text-rose-light font-mono"
            title={(data.unconvertible_currency_examples ?? [])
              .map((e) => `${e.name} (${e.currency})`).join(', ')}
          >
            ⚠ {data.unconvertible_currency_count} unconvertible FX
          </span>
        )}
        {(data.zero_value_count ?? 0) > 0 && (
          <span
            className="text-amber-light font-mono"
            title={(data.zero_value_examples ?? []).map((e) => e.name).join(', ')}
          >
            ⚠ {data.zero_value_count} zero-value
          </span>
        )}
      </div>
      {divergences && divergences.flagged_count > 0 && (
        <div className="mt-2 pt-2 border-t border-border/60">
          <div className="text-amber-light font-medium">
            ⚠ {divergences.flagged_count} price divergence{divergences.flagged_count === 1 ? '' : 's'}
            {' · '}
            <span className="font-mono">£{Math.round(divergences.total_value_flagged_gbp).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
            {' to verify'}
          </div>
          <div className="mt-1 flex flex-col gap-0.5">
            {divergences.holdings.slice(0, 6).map((h) => (
              <div key={`${h.symbol}-${h.name}`} className="flex flex-wrap items-baseline gap-x-2 text-[11px] text-text-muted">
                <span className="text-text-secondary truncate max-w-[14rem]" title={h.name}>{h.name}</span>
                <span className="font-mono text-text-primary">
                  stored £{h.stored_price.toLocaleString()} → market £{h.market_price.toLocaleString()}
                </span>
                <span className="font-mono">£{Math.round(h.value_gbp).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                <span className="text-text-muted/80 italic">{h.hint}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {unpriceable && (unpriceable.count ?? 0) > 0 && (
        <div className="mt-2 pt-2 border-t border-border/60">
          <div className="text-rose-light font-medium">
            ⛔ {unpriceable.count} holding{unpriceable.count === 1 ? '' : 's'} no free source can price
            {' · '}
            <span className="font-mono">
              £{Math.round(unpriceable.unpriceable_value_gbp ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </span>
          </div>
          <div className="text-[11px] text-text-muted/90 mt-0.5">
            FT has no tearsheet for these ISINs and no free fallback resolves them.
            They will not refresh on their own — they need a manual mark.
          </div>
          <div className="mt-1 flex flex-col gap-0.5">
            {(unpriceable.holdings ?? []).slice(0, 6).map((h) => (
              <div key={h.isin} className="flex flex-wrap items-baseline gap-x-2 text-[11px] text-text-muted">
                <span className="text-text-secondary truncate max-w-[14rem]" title={h.name}>{h.name}</span>
                <span className="font-mono text-text-muted/80">{h.isin}</span>
                <span className="font-mono text-text-primary">
                  £{Math.round(h.value_gbp ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </span>
                <span className="font-mono text-rose-light/90">
                  stuck {(h.last_price_update ?? '').slice(0, 10) || 'never priced'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
